/**
 * New support ticket form — `/app/support/new`. Reachable only when the current
 * admin has `support.submit` (canSubmit from GET /support/access); view-only
 * admins are bounced back to the list. The org + submitter identity are NOT in
 * this form — the ChMS backend derives them from the session and signs them into
 * the EMS request. Handles the 503 "support unconfigured" state gracefully.
 */
import { ApiError } from '@fc/core'
import { Button, Card, Input, Label, Spinner } from '@fc/ui'
import { useMutation } from '@tanstack/react-query'
import { ArrowLeft, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createTicket, type TicketPriority } from '../../lib/support-api'
import { isUnconfigured, SupportHeader, SupportUnconfigured, useSupportAccess } from './shared'

const textareaClass =
  'flex w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'placeholder:text-dim shadow-sm transition-colors focus-visible:border-secondary ' +
  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/40'

const selectClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

const PRIORITIES: TicketPriority[] = ['low', 'normal', 'high', 'urgent']

export function SupportNew() {
  const navigate = useNavigate()
  const access = useSupportAccess()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TicketPriority>('normal')

  const submit = useMutation({
    mutationFn: () =>
      createTicket({ title: title.trim(), description: description.trim(), priority }),
    onSuccess: (created) => navigate(`/app/support/${created.ticket_id}`),
  })

  if (isUnconfigured(access.error) || isUnconfigured(submit.error)) {
    return (
      <div className="space-y-6">
        <SupportHeader />
        <SupportUnconfigured />
      </div>
    )
  }

  if (access.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-muted">
        <Spinner /> Checking access…
      </div>
    )
  }

  // View-only admins must not reach the form.
  if (access.data && !access.data.canSubmit) {
    return (
      <div className="space-y-6">
        <SupportHeader />
        <Card>
          <div className="space-y-3 py-20 text-center">
            <p className="text-sm text-muted">
              You don’t have permission to submit support tickets.
            </p>
            <Button variant="secondary" onClick={() => navigate('/app/support')}>
              Back to support
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  const canSend = title.trim().length > 0 && description.trim().length > 0 && !submit.isPending
  const errorMessage =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.error
        ? 'Couldn’t submit the ticket. Please try again.'
        : null

  return (
    <div className="space-y-6">
      <Link
        to="/app/support"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" />
        Back to tickets
      </Link>

      <SupportHeader />

      <Card className="max-w-2xl">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSend) submit.mutate()
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="ticket-title">Title</Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ticket-priority">Priority</Label>
            <select
              id="ticket-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              className={selectClass}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ticket-description">Description</Label>
            <textarea
              id="ticket-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              placeholder="Describe what’s happening, what you expected, and any steps to reproduce."
              className={textareaClass}
            />
          </div>

          {errorMessage && (
            <p className="flex items-center gap-1.5 text-sm text-danger" role="alert">
              <TriangleAlert className="size-4 shrink-0" />
              {errorMessage}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate('/app/support')}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSend}>
              {submit.isPending ? <Spinner /> : null}
              Submit ticket
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
